import {CommentsCategory} from '@domain/comments'
import {Loading, ControlledCommentBox, type ControlledCommentProps} from '@hm/fundamental'
import {useState} from 'react'
import {useTranslation} from 'react-i18next'

import {ShutdownDetailCollapsible} from './ShutdownDetailCollapsible'

import {
  useAddComment,
  useEditComment,
  useDeleteComment
} from '@refra/modules/common/hooks/mutations'
import {useFundamentalComments} from '@refra/modules/common/hooks/queries'
import {logger} from '@refra/modules/common/utils'
import {useUrlParam} from '@refra/modules/routing/hooks'

interface ShutdownDetailCommentsProps {
  shutdownId: number
  kilnUpmId: string
  staticPartId: string
  isReadOnly?: boolean
}

export const ShutdownDetailComments: React.FC<ShutdownDetailCommentsProps> = ({
  shutdownId,
  kilnUpmId,
  staticPartId,
  isReadOnly = false
}) => {
  const {t} = useTranslation()
  const plantCode = useUrlParam('plantCode')

  const [currentComment, setCurrentComment] = useState('')

  const commentInstanceId = `${kilnUpmId}-${staticPartId}-${shutdownId}`

  const comments = useFundamentalComments({
    commentsCategory: CommentsCategory.StaticPartReplacements,
    commentCategoryInstanceIds: [commentInstanceId]
  })

  const {mutateAsync: addComment, isPending: isAddingComment} = useAddComment()
  const {mutateAsync: editComment, isPending: isEditingComment} = useEditComment()
  const {mutateAsync: deleteComment, isPending: isDeletingComment} = useDeleteComment()

  const handleAddComment = async () => {
    if (currentComment.trim().length === 0) return
    await addComment({
      plantCode,
      commentsCategory: CommentsCategory.StaticPartReplacements,
      commentCategoryInstanceIds: commentInstanceId,
      comment: currentComment
    }).catch((e) => {
      logger.extras({details: e.message}).error('Adding static part replacement comment failed')
    })
    setCurrentComment('')
  }

  const handleEditComment = async (comment: ControlledCommentProps, value: string) => {
    if (value.length === 0) return
    await editComment({
      plantCode,
      commentsCategory: CommentsCategory.StaticPartReplacements,
      commentCategoryInstanceIds: commentInstanceId,
      commentId: comment.id,
      comment: value
    }).catch((e) => {
      logger.extras({details: e.message}).error('Editing static part replacement comment failed')
    })
  }

  const handleDeleteComment = async (commentId: number) => {
    await deleteComment({
      plantCode,
      commentsCategory: CommentsCategory.StaticPartReplacements,
      commentCategoryInstanceIds: commentInstanceId,
      commentId
    }).catch((e) => {
      logger.extras({details: e.message}).error('Deleting static part replacement failed')
    })
  }

  const commentsSectionTitle = `${t('common.comments')} (${comments?.length})`

  if (!comments) {
    return (
      <div className="flex justify-center">
        <Loading size="large" />
      </div>
    )
  }

  const areCommentsDisabled = isReadOnly || isAddingComment || isEditingComment || isDeletingComment

  return (
    <ShutdownDetailCollapsible title={commentsSectionTitle} defaultOpen={true}>
      <div className="flex flex-col gap-3 pl-3 pr-3">
        <ControlledCommentBox
          comments={comments}
          commentInputValue={currentComment}
          onCommentInputChange={(value) => setCurrentComment(value)}
          onSubmit={handleAddComment}
          onEdit={handleEditComment}
          onDelete={handleDeleteComment}
          disabled={areCommentsDisabled}
        />
      </div>
    </ShutdownDetailCollapsible>
  )
}
